import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { ContentfulImage } from '../../components/Contentful';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { useFullViewportBreakout } from '../../hooks/useFullViewportBreakout';
import './ArtImmersionDetailPage.css';

// Type definitions for better type safety
interface Artist {
  name: string;
  bio: string;
  website?: string;
}

interface ArtImmersion {
  title: string;
  location: string;
  date: string;
  heroImage: string;
  description: string;
  fullContent: string;
  gallery: string[];
  hasVideo: boolean;
  videoUrl?: string;
  artist: Artist;
}

interface InstagramPost {
  src: string;
  alt: string;
  isVideo?: boolean;
}

const ArtImmersionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Mock data - replace with actual data fetching from Contentful
  const getImmersionData = (id: string): ArtImmersion | null => {
    const immersions: { [key: string]: ArtImmersion } = {
      'ingrid-weyland-topographies': {
        title: 'Ingrid Weyland - Topographies of Fragility',
        location: 'awasi Iguazu',
        date: '03.02.2022',
        heroImage: images.propertyPages.ingridWeylandTopographies,
        description: 'The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.',
        fullContent: `
          <p>Ingrid Weyland, an Argentine-born artist and photographer, has developed a distinctive artistic voice that speaks to our contemporary relationship with the natural world. Her work represents a powerful intersection of environmental consciousness and artistic innovation.</p>

          <p>In "Topographies of Fragility," Weyland employs a unique technique that has become her signature: she applies crumpled images on top of her photographs to highlight the impact humans have on nature. This layering effect creates a visual metaphor for the damage and disruption that human activities cause to natural landscapes.</p>

          <p>The technique itself is both simple and profound. By physically manipulating printed photographs – crumpling, folding, and layering them – Weyland creates textural landscapes that mirror the scarred and damaged terrains left by human intervention. The resulting works are both beautiful and haunting, inviting viewers to consider their own relationship with the environment.</p>

          <p>Her work resonates particularly strongly in the context of Awasi Iguazu, where the pristine rainforest provides a stark contrast to the environmental degradation depicted in her art. The juxtaposition serves as both celebration and warning – a reminder of what we stand to lose if we don't change our approach to environmental stewardship.</p>

          <p>Weyland's exhibition at Awasi represents more than just an art show; it's a call to action, encouraging visitors to see the natural world with fresh eyes and to consider their role in its preservation.</p>
        `,
        gallery: [
          images.propertyPages.ingridWeylandTopographies,
          images.galleryWildlife,
          images.galleryVilla,
          images.villaInterior
        ],
        hasVideo: false,
        artist: {
          name: 'Ingrid Weyland',
          bio: 'Argentine-born artist and photographer known for her environmental consciousness and innovative photographic techniques.',
          website: 'www.ingridweyland.com'
        }
      },
      'teresa-marcaida-nature-art': {
        title: 'Teresa Marcaida - Nature, Art and Mankind',
        location: 'awasi Iguazu',
        date: '03.02.2022',
        heroImage: images.galleryWildlife,
        description: 'As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you.',
        fullContent: `
          <p>Teresa Marcaida's "Nature, Art and Mankind" explores the profound connections between human consciousness and the natural world. Her work invites us into moments of deep contemplation and communion with nature.</p>

          <p>"As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you." This quote, which opens her exhibition, encapsulates the essence of Marcaida's artistic philosophy.</p>

          <p>Through her multimedia installations and video works, Marcaida creates immersive experiences that slow down time and heighten awareness. Her pieces often incorporate natural sounds, ambient lighting, and organic materials to create environments where viewers can experience the kind of deep connection with nature that has become increasingly rare in our modern world.</p>

          <p>The exhibition at Awasi Iguazu provides the perfect setting for this work, as visitors can step from the gallery directly into the rainforest that inspired it. This seamless transition between art and nature is central to Marcaida's vision – art not as something separate from life, but as a lens through which to see life more clearly.</p>
        `,
        gallery: [
          images.galleryWildlife,
          images.galleryMain,
          images.villaInterior,
          images.galleryVilla
        ],
        hasVideo: true,
        videoUrl: '/videos/teresa-marcaida.mp4',
        artist: {
          name: 'Teresa Marcaida',
          bio: 'Contemporary artist working with multimedia installations and environmental themes.',
          website: 'www.teresamarcaida.com'
        }
      }
    };

    return immersions[id] || null;
  };

  const immersion = getImmersionData(id || '');


  // Allow full viewport breakout for page break images
  useEffect(() => {
    // Add class to body to allow horizontal overflow for full viewport breakout
    document.body.classList.add('allow-full-viewport-breakout');

    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove('allow-full-viewport-breakout');
    };
  }, []);

  // Initialize carousel interactions
  useEffect(() => {
    // Initialize drag scroll for the carousel
    const cleanup = initializeDragScroll('art-immersion-carousel');

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  if (!immersion) {
    return (
      <div className="art-detail-page">
        <SmartNavigation forceVariant="light" />
        <div className="not-found">
          <h1>Art Immersion Not Found</h1>
          <Link to="/art-immersions/">Back to Art Immersions</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="art-detail-page">
      {/* Navigation Header - matching art immersions page */}
      <SmartNavigation forceVariant="light" />

      {/* Orange vertical line accent - matching art immersions page */}
      <div className="vertical-accent-line" />

      {/* 1. Header Section - Figma Exact Layout */}
      <section className="art-detail-header">
        <div className="header-text-block">
          <div className="header-content">
            {/* Main Title - Using design system typography */}
            <h1 className="art-detail-title">
              {immersion.title}
            </h1>

            {/* Author + Date Block - Using design system typography */}
            <div className="art-detail-meta">
              <div className="meta-location-date">
                <span className="meta-location">{immersion.location.toUpperCase()}</span>
                <span className="meta-separator">-</span>
                <span className="meta-date">{immersion.date}</span>
              </div>
              <div className="meta-author">BY GABRIEL ORORKE</div>
            </div>
          </div>
        </div>

        {/* Social Icons - Positioned at bottom of section */}
        <div className="social-icons">
            <div className="social-icon twitter-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" fill="currentColor"/>
              </svg>
            </div>
            <div className="social-icon facebook-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="currentColor"/>
            </svg>
            </div>
            <div className="social-icon link-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
        </div>
      </section>

      {/* 2. Hero Image Section - Full width */}
      <section className="art-detail-hero-image">
        <img src={immersion.heroImage} alt={immersion.title} />
      </section>

      {/* Hero Image Caption */}
      <p className="hero-image-caption">
        Ingrid captures the iconic red soil surrounding the Iguazu Falls
      </p>

      {/* 3. Article Content - Matching Exact Figma Structure */}

      {/* Blog Block 1 */}
      <section className="blog-block" style={{ marginTop: '20px', paddingTop: '40px' }}>
        <div className="blog-content">
          {/* Quote 1 */}
          <div className="quote-section">
            <p className="quote-text">
              <span className="quote-content">"Initially, you are drawn to the beauty of the scene"</span> <span className="attribution">- says Ingrid Weyland.</span> <span className="quote-content">"Only afterwards, are you able to understand its vulnerable character."</span>
            </p>
          </div>

          {/* Body 2 */}
          <div className="body-section">
            <p>
              The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.
            </p>
            <p>
              Ingrid recently travelled to Northern Argentina to capture the natural surroundings of the Atlantic Rainforest where Awasi Iguazu is located.
            </p>
          </div>

          {/* Quote 3 */}
          <div className="quote-section">
            <p className="quote-text">
              <span className="quote-content">"I mean for the viewer to be challenged by my work. It requires an active participation, needs more than one viewing."</span> <span className="attribution">– says Ingrid in a takeover she did for Aesthetica Magazine using her images from Iguazu.</span>
            </p>
          </div>

          {/* Body 4 */}
          <div className="body-section">
            <p>
              So, how did Ingrid come up with her individual way of producing photographs?
            </p>
            <p>
              "I had some images that had been printed in poor quality," she explains, "so I crumpled them in frustration and tossed them in the bin. I then felt bad, so I went over to the bin and took the prints out again."
            </p>
            <p>
              This was the beginning of what would become a defining part of her work: playing with crumpled images, moulding them and placing them on top of the original photograph, and re-photographing the scene.
            </p>
          </div>
        </div>

      </section>

        {/* Image with separated caption */}
        <div className="image-section image-inline">
          <img src={images.propertyPages.ingridWeylandForest} alt="There has been much deforestation in the area" />
        </div>

        {/* Caption aligned with body text */}
        <p className="image-caption">
          There has been much deforestation in the area, more so on the Brazilian side than the Argentine, but on both sides the biodiversity is delicate.
        </p>

      <section className="blog-block">

        {/* Text Block with Quotes */}
        <div className="body-section">
          <p>
            Our commitment to sustainability drove us to make Awasi a <span className="underlined">Carbon Neutral experience.</span> We protect 340 hectares of native woodland in Iguazú and Patagonia. These areas absorb over 10,000 tonnes of CO2 per year, a greater amount than the emissions generated by our three lodges, and the travel footprint of our guests.
          </p>
          <p>
            We also work to conserve the surrounding ecosystems. For example, <span className="underlined">reintroducing native species</span> and implementing concrete social initiatives such as <span className="underlined">workshops for local children</span> to encourage protecting the surrounding environment.
          </p>
          <p className="italic-text">
            We were drawn to Ingrid's use of art to encourage people question the status quo.
          </p>
        </div>
      </section>

      {/* Page Break Image 1 */}
      <section className="page-break-image">
        <img src={images.propertyPages.pageBreakImage} alt="Page break showing natural landscape" />
        <p className="page-break-caption">
          During her stay at Awasi Iguazu, Ingrid spent the days photographing the contrasting natural surroundings.
        </p>
      </section>

      {/* Blog Block 2 */}
      <section className="blog-block" style={{ paddingBottom: '48px' }}>
        <div className="blog-content">
          {/* Quote */}
          <div className="quote-section">
            <p className="quote-text">
              "My intention is to highlight the beauty and man-made ravage at the same time, intertwined, in my desire for the preservation of the little which remains untouched and therefore the desperate need to protect our natural world."
            </p>
          </div>

          {/* Body */}
          <div className="body-section">
            <p>
              Ingrid says her background as a graphic designer "helps a lot with aesthetics". Back in 2019, she first started manipulating paper.
            </p>
            <p>
              "The idea of paper kept floating around in my brain," she says. "The inspiration came from looking at the multifaceted surface of an iceberg. I suddenly thought it looks like crumpled paper. I like that people have to look it and find out and it takes two different readings rather than it jumping out in your face straight away. Crumpling the image is violent, but it serves to draw attention to a place of natural beauty."
            </p>
          </div>
        </div>

        {/* Image + Fine Print */}
        <div className="image-section image-inline">
          <img src={images.propertyPages.iguazuWaterfalls} alt="Iguazu waterfalls landscape" />
        </div>

        {/* Image Caption as separate element */}
        <p className="image-caption body-aligned">
          Iguazu is famous for its waterfalls, but few people are aware of the precious biodiversity of the Misiones region.
        </p>

        {/* Quote + Body */}
        <div className="blog-content">
          <div className="quote-section">
            <p className="quote-text">
              "A crumpled piece of paper can never regain its original shape; the trace persists. In the same way, nature which is disrespectfully invaded is forever broken, and many times unrecoverable."
            </p>
          </div>

          <div className="body-section">
            <p>
              Ingrid has a love for cold climates, Iceland is a country that keeps pulling her back. "I prefer the darker shades, not the bright blues but rather the depth of greys," she says.
            </p>
            <p>
              Up in Iguazu her colour palette is mainly the green of the Atlantic Rainforest contrasted against a cloudy sky.
            </p>
            <p>
              "I enjoyed being in an area which is protected and where sustainability is valued," says Ingrid who never shies from witnessing the magnificence of nature but also thinking about its vulnerability.
            </p>
            <p>
              "I started to work on enacting physically violent gestures on my printed landscape images, twisting and reshaping them until they became something utterly different," explains the artist. "In between trying alternate ways of crumpled imageries, I lay them on top of the same photographic vista, and rephotograph."
            </p>
          </div>
        </div>
      </section>

      {/* Page Break Image 2 */}
      <section className="page-break-image" style={{ marginTop: '0' }}>
        <video
          src={images.rectangle22}
          autoPlay
          loop
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <p className="page-break-caption">
          The region of Misiones is a biodiversity hotspot home to endemic species of flora and fauna.
        </p>
      </section>

      {/* Blog Block 3 - Final */}
      <section className="blog-block">
        <div className="blog-content">
          {/* Quote */}
          <div className="quote-section">
            <p className="quote-text">
              "It's amazing to notice that paper resists at first, it fights back, it does not want to be destroyed, but at last, it has to give in …"
            </p>
          </div>

          {/* Body */}
          <div className="body-section">
            <p>
              Ultimately, Ingrid raises a question through her art:
            </p>
            <p>
              "Can art have a substantial role in raising awareness of the critical moment we're in?"
            </p>
            <p>
              "Yes," she says. "I believe it can."
            </p>
            <p>
              Ingrid Weyland is widely recognised for her work which provides beauty, but also challenges the viewer. Her awards and accolades include: <span className="underlined">Ashurst Emerging Artist Photography Prize 2021</span>; <span className="underlined">Rhonda Wilson Award 2021</span>; <span className="underlined">Klompching Fresh 2021</span> and <span className="underlined">Lens Culture Critics Choice 2021</span>.
            </p>
          </div>
        </div>

        {/* Image + Fine Print */}
        <div className="image-section image-inline">
          <img src={images.propertyPages.oneOfThe14Villas} alt="One of the 14 villas at Awasi Iguazu" />
        </div>

        {/* Image Caption as separate element */}
        <p className="image-caption body-aligned">
          One of the 14 villas at Awasi Iguazu. Photo: João Canziani
        </p>

        {/* Final Body */}
        <div className="body-section">
          <p>
            Awasi Iguazu is a <span className="underlined">Relais & Chateaux hotel in Northern Argentina</span>, just 15 minutes from the <span className="underlined">Iguazu Falls</span>. The lodge has just 14 rooms and the focus is on providing the very best accommodation, gastronomy and excursions. Each room (at all three Awasi properties) is given a personal 4×4 vehicle and private guide for the duration of each stay. This means excursions are private and based around each guest's individual requirements.
          </p>
        </div>

        {/* Call to Action Buttons */}
        <div className="cta-buttons">
          <HandDrawnButton
            text="GO TO THE TOP"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            className="cta-button primary"
          />
          <HandDrawnButton
            text="BACK TO BLOG"
            onClick={() => window.history.back()}
            variant="brand"
            color="off-white"
            animation="stroke-to-stroke"
            className="cta-button secondary"
          />
        </div>
      </section>


      {/* 4. You Might Enjoy Carousel Section - Matching In-Page Pattern */}
      <section className="you-might-enjoy-section">
        <div className="you-might-enjoy-content">
          {/* Text Content Section - Title above like other in-page carousels */}
          <div className="you-might-enjoy-text-content">
            <h2 className="you-might-enjoy-title">You might enjoy</h2>
          </div>

          {/* Carousel Section - Cards below */}
          <div className="you-might-enjoy-carousel" id="art-immersion-carousel">
            <div className="carousel-card">
              <div className="card-image-container">
                <img src={immersion.gallery[0]} alt="Ingrid Weyland - Topographies of Fragility" />
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3 className="card-title">Ingrid Weyland - Topographies of Fragility</h3>
                  <div className="card-meta">
                    <span className="meta-location">AWASI IGUAZU</span>
                    <span className="meta-separator">-</span>
                    <span className="meta-date">03.02.2022</span>
                  </div>
                </div>
                <p className="card-description">
                  The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.
                </p>
                <div className="card-links">
                  <Link to={`/art-immersions/ingrid-weyland-topographies/`} className="read-more-link">
                    READ MORE
                  </Link>
                </div>
              </div>
            </div>

            <div className="carousel-card">
              <div className="card-image-container">
                <img src={immersion.gallery[1]} alt="Teresa Marcaida - Nature, Art and Mankind" />
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3 className="card-title">Teresa Marcaida - Nature, Art and Mankind</h3>
                  <div className="card-meta">
                    <span className="meta-location">AWASI IGUAZU</span>
                    <span className="meta-separator">-</span>
                    <span className="meta-date">03.02.2022</span>
                  </div>
                </div>
                <p className="card-description">
                  As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you.
                </p>
                <div className="card-links">
                  <Link to={`/art-immersions/teresa-marcaida-nature-art/`} className="read-more-link">
                    READ MORE
                  </Link>
                </div>
              </div>
            </div>

            <div className="carousel-card">
              <div className="card-image-container">
                <img src={immersion.gallery[2]} alt="Rainer Gross - Double Takes" />
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3 className="card-title">Rainer Gross - "Double Takes"</h3>
                  <div className="card-meta">
                    <span className="meta-location">AWASI IGUAZU</span>
                    <span className="meta-separator">-</span>
                    <span className="meta-date">03.02.2022</span>
                  </div>
                </div>
                <p className="card-description">
                  Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo.
                </p>
                <div className="card-links">
                  <Link to={`/art-immersions/rainer-gross-double-takes/`} className="read-more-link">
                    READ MORE
                  </Link>
                </div>
              </div>
            </div>

            <div className="carousel-card">
              <div className="card-image-container">
                <img src={immersion.heroImage} alt="Iliana Díaz López - Portales del Ara Pyau" />
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3 className="card-title">Iliana Díaz López - "Portales del Ara Pyau"</h3>
                  <div className="card-meta">
                    <span className="meta-location">AWASI IGUAZU</span>
                    <span className="meta-separator">-</span>
                    <span className="meta-date">03.02.2022</span>
                  </div>
                </div>
                <p className="card-description">
                  Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo.
                </p>
                <div className="card-links">
                  <Link to={`/art-immersions/iliana-diaz-portales/`} className="read-more-link">
                    READ MORE
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* 6. Instagram Section - matching other pages */}
      <InstagramSection />

      {/* 7. Footer - matching other pages */}
      <Footer variant="default" />
    </div>
  );
};

export default ArtImmersionDetailPage;